import apiUtil from "@/util/apiUtil";
import {ISODateString} from "@/util/dateUtil";
import {
    ICoachInsightsResponse,
    IImpactedExperiencesResponse,
    ILaborScheduleResponse,
    ILaborStaffingSuggestionRequest,
    ILaborStaffingSuggestionResponse,
    ISosBusinessImpactResponse,
    IStaffCoverageResponse
} from "./coachInsightsModels";
import {deriveRecommendedAction, estimateRoi} from "./coachInsightsUtils";

const BASE_URL = "/api/CoachInsights";

const coachInsightsAPI = {
    async getCoachInsights(
        businessUnitId: string,
        businessDate: ISODateString,
        abortSignal?: AbortSignal
    ): Promise<ICoachInsightsResponse | null> {
        const url = `${BASE_URL}/${businessUnitId}/${businessDate}`;
        const response = await apiUtil.fetchData<ICoachInsightsResponse>(
            url,
            abortSignal
        );
        if (typeof response !== "object" || response === null) return null;
        if (response.speedOfServiceInsights) {
            response.speedOfServiceInsights = response.speedOfServiceInsights
                .map((insight) => {
                    const derived = deriveRecommendedAction(insight);
                    return {
                        ...insight,
                        recommendedAction: insight.recommendedAction ?? derived.text,
                        suggestedFeedbackAction:
                            insight.suggestedFeedbackAction ?? derived.feedbackAction,
                        estimatedRoi: insight.estimatedRoi ?? estimateRoi(insight)
                    };
                })
                .sort((a, b) => b.estimatedRoi - a.estimatedRoi);
        }
        return response;
    },

    async getImpactedExperiences(
        locationPk: number,
        businessDate: ISODateString,
        startHour: number,
        endHour: number,
        abortSignal?: AbortSignal
    ): Promise<IImpactedExperiencesResponse> {
        const url = `${BASE_URL}/ImpactedExperiences/${locationPk}/${businessDate}/${startHour}/${endHour}`;
        return await apiUtil.fetchData<IImpactedExperiencesResponse>(
            url,
            abortSignal
        );
    },

    async getStaffCoverage(
        locationPk: number,
        businessDate: ISODateString,
        abortSignal?: AbortSignal
    ): Promise<IStaffCoverageResponse> {
        const url = `${BASE_URL}/StaffCoverage/${locationPk}/${businessDate}`;
        return await apiUtil.fetchData<IStaffCoverageResponse>(url, abortSignal);
    },

    async getLaborSchedule(
        businessUnitId: string,
        weekStartDate: ISODateString,
        abortSignal?: AbortSignal
    ): Promise<ILaborScheduleResponse> {
        const url = `${BASE_URL}/LaborSchedule/${businessUnitId}/${weekStartDate}`;
        return await apiUtil.fetchData<ILaborScheduleResponse>(url, abortSignal);
    },

    async getLaborScheduleStaffingSuggestion(
        request: ILaborStaffingSuggestionRequest,
        abortSignal?: AbortSignal
    ): Promise<ILaborStaffingSuggestionResponse> {
        const url = `${BASE_URL}/LaborScheduleStaffingSuggestion`;
        return await apiUtil.postData<ILaborStaffingSuggestionResponse>(
            url,
            request,
            abortSignal
        );
    },

    async getSosBusinessImpact(
        locationPk: number,
        businessDate: ISODateString,
        startHour: number,
        endHour: number,
        abortSignal?: AbortSignal
    ): Promise<ISosBusinessImpactResponse> {
        const url = `${BASE_URL}/BusinessImpact/${locationPk}/${businessDate}/${startHour}/${endHour}`;
        return await apiUtil.fetchData<ISosBusinessImpactResponse>(url, abortSignal);
    }
};

export default coachInsightsAPI;
